import { useEffect, useState } from "react";
import { Truck, Leaf, Clock, CircleAlert } from "lucide-react";
import { StoreShell } from "@/components/store/StoreShell";
import { ProductCard } from "@/components/store/ProductCard";
import { CutChoppedSection } from "@/components/store/CutChoppedSection";
import { FestivalBanners } from "@/components/store/FestivalBanners";
import { useStore } from "@/context/StoreContext";
import { api, imgUrl } from "@/lib/api";

export default function Home() {
  const { config, area, setAreaOpen } = useStore();
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState("all");
  const [slots, setSlots] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/slots/today").then((r) => setSlots(r.data));
  }, []);

  const shown = active === "all" ? products : products.filter((p) => p.category_id === active);
  const nextSlot = slots.find((s) => s.available);

  const openBanner = (b) => {
    const p = b.link_type === "product" ? products.find((x) => x.id === b.link_id) : null;
    setActive(p ? p.category_id : b.link_id);
    setTimeout(() => {
      const el = p ? document.querySelector(`#harvest [data-testid="product-card-${p.id}"]`) : document.getElementById("harvest");
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: p ? "center" : "start" });
      if (p) { el.classList.add("ring-4", "ring-accent"); setTimeout(() => el.classList.remove("ring-4", "ring-accent"), 2200); }
    }, 80);
  };

  return (
    <StoreShell>
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-6 sm:pt-10 grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-6">
        <div className="md:col-span-8 relative rounded-3xl overflow-hidden min-h-[340px] sm:min-h-[420px] grain rise" data-testid="hero">
          <img src="/images/hero.jpg" alt="Fresh vegetables" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="relative h-full flex flex-col justify-end p-6 sm:p-10 text-white min-h-[340px] sm:min-h-[420px]">
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#d9e3b4]">Today's mandi · {config?.today}</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[0.95] mt-3 max-w-xl text-white">
              Fresh sabzi, priced every morning, at your door today.
            </h1>
            <p className="mt-4 text-white/85 max-w-md text-sm sm:text-base">From our own stock in Vadodara, packed and delivered by our own riders.</p>
          </div>
        </div>
        <div className="md:col-span-4 grid grid-cols-2 md:grid-cols-1 gap-4 sm:gap-6">
          <div className="rounded-3xl bg-primary text-white p-5 sm:p-6 flex flex-col justify-between rise" style={{ animationDelay: "120ms" }} data-testid="delivery-info-tile">
            <Truck className="w-6 h-6 text-[#d9e3b4]" strokeWidth={1.5} />
            <div className="mt-6">
              <p className="text-xs uppercase tracking-[0.15em] text-white/60 font-bold">Next delivery slot</p>
              <p className="font-display text-2xl sm:text-3xl mt-1" data-testid="next-slot">{nextSlot ? `${nextSlot.start}–${nextSlot.end}` : "Tomorrow"}</p>
              <p className="text-xs text-white/70 mt-1">{nextSlot ? `Order before ${nextSlot.cutoff} · ${nextSlot.remaining} spots left` : "Today's slots are closed or full"}</p>
            </div>
          </div>
          <button onClick={() => setAreaOpen(true)} className="text-left rounded-3xl bg-white border border-stone-200 p-5 sm:p-6 flex flex-col justify-between hover:border-primary/40 transition-colors rise" style={{ animationDelay: "200ms" }} data-testid="area-tile">
            <Leaf className="w-6 h-6 text-secondary" strokeWidth={1.5} />
            <div className="mt-6">
              <p className="text-xs uppercase tracking-[0.15em] text-stone-500 font-bold">Delivering to</p>
              <p className="font-display text-2xl sm:text-3xl text-primary mt-1">{area ? area.pincode : "Choose area"}</p>
              <p className="text-xs text-stone-500 mt-1 truncate">{area ? area.name : "Select zones only"}</p>
            </div>
          </button>
        </div>
      </section>

      {config && !config.ordering_open && (
        <div className="max-w-7xl mx-auto px-4 sm:px-8 mt-6">
          <div className="flex items-center gap-3 rounded-2xl bg-accent/10 border border-accent/30 text-accent px-5 py-4 font-semibold" data-testid="ordering-closed-banner">
            <CircleAlert className="w-5 h-5" strokeWidth={1.5} /> {config.closed_reason} You can still browse.
          </div>
        </div>
      )}

      <FestivalBanners onOpen={openBanner} />

      <CutChoppedSection products={products} onSeeAll={() => { setActive("cut"); document.getElementById("harvest")?.scrollIntoView({ behavior: "smooth" }); }} />

      <section id="harvest" className="max-w-7xl mx-auto px-4 sm:px-8 mt-10 sm:mt-14 scroll-mt-16">
        <div className="flex items-end justify-between gap-4 mb-5">
          <div>
            <p className="text-xs tracking-[0.1em] uppercase text-stone-500 font-bold flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Prices updated today</p>
            <h2 className="font-display text-3xl sm:text-4xl text-primary font-semibold mt-1">Today's harvest</h2>
          </div>
        </div>
        <div className="sticky top-16 z-40 bg-[#F9F8F4] py-3 -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="category-bar">
            {[{ id: "all", name: "All", image: "/images/exotic.jpg" }, ...cats.filter((c) => products.some((p) => p.category_id === c.id))].map((c) => (
              <button key={c.id} onClick={() => setActive(c.id)} data-testid={`category-chip-${c.id}`}
                className={`shrink-0 flex items-center gap-2 rounded-full pl-1 pr-4 py-1 border text-sm font-semibold transition-colors ${active === c.id ? "bg-primary text-white border-primary" : "bg-white border-stone-200 text-primary hover:border-primary/40"}`}>
                <img src={imgUrl(c.image)} alt="" className="w-8 h-8 rounded-full object-cover" />
                {c.name}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6 mt-4" data-testid="product-grid">
          {shown.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
        </div>
        {shown.length === 0 && <p className="text-stone-500 py-10" data-testid="no-products">No products in this category today.</p>}
      </section>
    </StoreShell>
  );
}
